"use client";

/**
 * ResolverContext — holds `collected` values on the Hub side.
 *
 * No state machine. Step components read the current StepDescriptor (computed
 * from collected) and call setters to merge new values. Re-render triggers
 * recomputation via the useResolverStep hook.
 */

import { createContext, useCallback, useContext, useMemo, useState } from "react";

// Direct-import: avoid the barrel `@/lib/actions/resolution` which re-exports
// server-only functions (`nextStep`, `runResolvedAction`, `backfillMissingFields`)
// that drag `getServiceSupabaseOrThrow` into the client bundle.
import { buildInitialCollected } from "@/lib/actions/resolution/initialCollected";
import { applyCollectedPatch } from "@/lib/actions/resolution/collectedPatch";
import type {
  BulkPick,
  CollectedValues,
  ExtraValues,
  FieldValues,
  WalkPicks,
} from "@/lib/actions/resolution/types";
import type { ResolverEntityKey } from "@/lib/actions/relationships/types";

interface ResolverProviderProps {
  actionId: string;
  sourceEntity: ResolverEntityKey;
  sourceId: string;
  onClose: () => void;
  children: React.ReactNode;
}

interface ResolverContextValue {
  actionId: string;
  collected: CollectedValues;
  setPick: (stepIndex: number, candidateId: string) => void;
  setBulkPick: (ids: string[]) => void;
  setFields: (values: FieldValues) => void;
  setExtras: (values: ExtraValues) => void;
  setConfirmed: (value: boolean) => void;
  /** Merge server auto-advance picks (e.g. deal → company) into client state. */
  mergeServerCollected: (incoming: CollectedValues) => void;
  reset: () => void;
  close: () => void;
}

const ResolverContext = createContext<ResolverContextValue | null>(null);

export function ResolverProvider({
  actionId,
  sourceEntity,
  sourceId,
  onClose,
  children,
}: ResolverProviderProps) {
  const [collected, setCollected] = useState<CollectedValues>(() =>
    buildInitialCollected({ actionId, sourceEntity, sourceId }),
  );

  const setPick = useCallback((stepIndex: number, candidateId: string) => {
    setCollected((prev) => {
      const picks: WalkPicks = { ...prev.picks, [stepIndex]: candidateId };
      return { ...prev, picks, bulkPick: undefined };
    });
  }, []);

  const setBulkPick = useCallback((ids: string[]) => {
    const bulk: BulkPick = { ids };
    setCollected((prev) => ({ ...prev, bulkPick: bulk }));
  }, []);

  const setFields = useCallback((values: FieldValues) => {
    setCollected((prev) => ({
      ...prev,
      fields: { ...prev.fields, ...values },
    }));
  }, []);

  const setExtras = useCallback((values: ExtraValues) => {
    setCollected((prev) => ({
      ...prev,
      extras: { ...prev.extras, ...values },
    }));
  }, []);

  const setConfirmed = useCallback((value: boolean) => {
    setCollected((prev) => ({ ...prev, confirmed: value }));
  }, []);

  const mergeServerCollected = useCallback((incoming: CollectedValues) => {
    setCollected((prev) =>
      applyCollectedPatch(prev, {
        picks: incoming.picks,
        bulkPick: incoming.bulkPick,
        fields: incoming.fields,
        extras: incoming.extras,
        confirmed: incoming.confirmed,
      }),
    );
  }, []);

  const reset = useCallback(() => {
    setCollected(buildInitialCollected({ actionId, sourceEntity, sourceId }));
  }, [actionId, sourceEntity, sourceId]);

  const close = useCallback(() => {
    onClose();
  }, [onClose]);

  const value = useMemo<ResolverContextValue>(
    () => ({
      actionId,
      collected,
      setPick,
      setBulkPick,
      setFields,
      setExtras,
      setConfirmed,
      mergeServerCollected,
      reset,
      close,
    }),
    [
      actionId,
      collected,
      setPick,
      setBulkPick,
      setFields,
      setExtras,
      setConfirmed,
      mergeServerCollected,
      reset,
      close,
    ],
  );

  return (
    <ResolverContext.Provider value={value}>
      {children}
    </ResolverContext.Provider>
  );
}

export function useResolverContext(): ResolverContextValue {
  const ctx = useContext(ResolverContext);
  if (!ctx) {
    throw new Error(
      "useResolverContext must be used within a <ResolverProvider>",
    );
  }
  return ctx;
}
